
import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import ProfessionList from "@/components/professions/ProfessionList";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Building2, Users, CheckCircle2, Phone, MapPin, Award, TrendingUp, Clock, Recycle, ShieldCheck, Target } from "lucide-react";
import { Link } from "react-router-dom";

const MetiersIndex = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Solutions de recyclage par métier",
    "description": "Solutions de recyclage et valorisation des déchets adaptées à chaque métier professionnel",
    "provider": {
      "@type": "Organization",
      "name": "Groupe Fabre Environnement",
      "url": "https://groupefabre.com"
    },
    "serviceType": "Gestion des déchets par secteur d'activité",
    "areaServed": {
      "@type": "Place",
      "name": "France"
    },
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock"
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <SEO 
        title="Solutions de Recyclage par Métier - Déchets Professionnels | Groupe Fabre Environnement"
        description="✓ Solutions sur-mesure par métier ✓ Garagistes, restaurants, bureaux ✓ Collecte spécialisée ✓ Conformité réglementaire ✓ 15+ secteurs couverts ✓ Devis gratuit"
        canonical="/nos-metiers"
        keywords="solutions recyclage métier, déchets professionnels, gestion déchets artisans, valorisation secteur activité, conformité déchets pro"
      />
      <script type="application/ld+json">
        {JSON.stringify(jsonLd)}
      </script>
      
      <Navbar />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-br from-blue-600 via-indigo-600 to-fabre-green text-white py-20 mt-20 overflow-hidden">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-48 translate-x-48"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full translate-y-32 -translate-x-32"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-6 bg-white/20 text-white border-white/30 hover:bg-white/30">
                <Building2 className="w-4 h-4 mr-2" />
                Solutions Professionnelles Sur-Mesure
              </Badge>
              <h1 className="text-5xl md:text-6xl font-heading font-bold mb-6 leading-tight">
                Solutions de Recyclage
                <span className="text-blue-200"> par Métier</span>
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-gray-100 leading-relaxed">
                Des solutions de valorisation des déchets adaptées à votre activité professionnelle. 
                Chaque métier a ses spécificités, nous avons la solution.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <Link to="/contact">
                  <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 font-semibold px-8">
                    <Phone className="w-5 h-5 mr-2" />
                    Demander un devis gratuit
                  </Button>
                </Link>
                <Link to="/sites">
                  <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 shadow-xl bg-white/10 backdrop-blur-sm font-semibold">
                    <MapPin className="w-5 h-5 mr-2" />
                    Nos sites proches
                  </Button>
                </Link>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-200 mb-2">15+</div>
                  <p className="text-gray-200">Métiers accompagnés</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-200 mb-2">100%</div>
                  <p className="text-gray-200">Conformité garantie</p>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-200 mb-2">30+</div>
                  <p className="text-gray-200">Ans d'expertise</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Avantages Section */}
        <div className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-heading font-bold mb-4 text-fabre-gray">
                  Pourquoi des solutions par métier ?
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  Chaque activité professionnelle génère des déchets spécifiques qui nécessitent 
                  une approche adaptée pour optimiser leur valorisation.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8 mb-16">
                <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300 border-l-4 border-l-blue-500">
                  <CardHeader className="pb-4">
                    <CardTitle className="flex items-center text-blue-600 text-xl">
                      <Target className="mr-3 h-7 w-7" />
                      Solutions Ciblées
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 mb-4">
                      Analyse précise des déchets générés par votre activité pour proposer 
                      la solution la plus adaptée et efficace.
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-center text-sm">
                        <CheckCircle2 className="w-4 h-4 text-blue-500 mr-2" />
                        Étude personnalisée
                      </li>
                      <li className="flex items-center text-sm">
                        <CheckCircle2 className="w-4 h-4 text-blue-500 mr-2" />
                        Solutions optimales
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300 border-l-4 border-l-green-500">
                  <CardHeader className="pb-4">
                    <CardTitle className="flex items-center text-green-600 text-xl">
                      <ShieldCheck className="mr-3 h-7 w-7" />
                      Conformité Totale
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 mb-4">
                      Respect de toutes les réglementations spécifiques à votre secteur 
                      d'activité et accompagnement administratif complet.
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-center text-sm">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2" />
                        Veille réglementaire
                      </li>
                      <li className="flex items-center text-sm">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mr-2" />
                        Documentation complète
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300 border-l-4 border-l-purple-500">
                  <CardHeader className="pb-4">
                    <CardTitle className="flex items-center text-purple-600 text-xl">
                      <TrendingUp className="mr-3 h-7 w-7" />
                      Valorisation Optimale
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 mb-4">
                      Maximisation de la valorisation de vos déchets grâce à notre expertise 
                      sectorielle et nos filières spécialisées.
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-center text-sm">
                        <CheckCircle2 className="w-4 h-4 text-purple-500 mr-2" />
                        Filières adaptées
                      </li>
                      <li className="flex items-center text-sm">
                        <CheckCircle2 className="w-4 h-4 text-purple-500 mr-2" />
                        Rentabilité optimisée
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>

              {/* Process Section */}
              <div className="bg-white rounded-2xl shadow-lg p-8 mb-16">
                <h2 className="text-3xl font-heading font-bold text-center mb-12 text-fabre-gray">
                  Notre démarche métier par métier
                </h2>
                
                <div className="grid md:grid-cols-4 gap-6">
                  <div className="text-center group">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-200 transition-colors">
                      <Target className="w-8 h-8 text-blue-600" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2 text-fabre-gray">1. Analyse</h3>
                    <p className="text-gray-600 text-sm">Identification des déchets spécifiques à votre métier</p>
                  </div>
                  
                  <div className="text-center group">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-200 transition-colors">
                      <Award className="w-8 h-8 text-blue-600" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2 text-fabre-gray">2. Solution</h3>
                    <p className="text-gray-600 text-sm">Proposition adaptée à vos contraintes</p>
                  </div>
                  
                  <div className="text-center group">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-200 transition-colors">
                      <Clock className="w-8 h-8 text-blue-600" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2 text-fabre-gray">3. Mise en œuvre</h3>
                    <p className="text-gray-600 text-sm">Déploiement rapide et formation équipes</p>
                  </div>
                  
                  <div className="text-center group">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-200 transition-colors">
                      <Recycle className="w-8 h-8 text-blue-600" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2 text-fabre-gray">4. Suivi</h3>
                    <p className="text-gray-600 text-sm">Accompagnement continu et optimisation</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Main Content */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-heading font-bold mb-4 text-fabre-gray">
                Des solutions adaptées à chaque métier
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Découvrez comment Groupe Fabre Environnement peut vous aider à valoriser 
                vos déchets professionnels avec des solutions personnalisées.
              </p>
            </div>
            
            <ProfessionList />

            {/* CTA Section */}
            <div className="mt-16 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl p-8 md:p-12 text-center">
              <h2 className="text-3xl font-heading font-bold mb-4">
                Votre métier n'est pas listé ?
              </h2>
              <p className="text-xl mb-8 text-blue-100">
                Nous étudions des solutions sur-mesure pour tous les secteurs d'activité
              </p>
              
              <div className="grid md:grid-cols-3 gap-8 mb-8">
                <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                  <Users className="w-12 h-12 text-blue-200 mx-auto mb-4" />
                  <h4 className="font-semibold text-lg mb-2">Conseil Expert</h4>
                  <p className="text-blue-100">Analyse personnalisée de vos besoins</p>
                </div>
                <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                  <Award className="w-12 h-12 text-blue-200 mx-auto mb-4" />
                  <h4 className="font-semibold text-lg mb-2">Solution Sur-Mesure</h4>
                  <p className="text-blue-100">Développement d'une approche adaptée</p>
                </div>
                <div className="bg-white/10 rounded-lg p-6 backdrop-blur-sm">
                  <ShieldCheck className="w-12 h-12 text-blue-200 mx-auto mb-4" />
                  <h4 className="font-semibold text-lg mb-2">Conformité Garantie</h4>
                  <p className="text-blue-100">Respect de toutes les réglementations</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 font-semibold px-8">
                    <Phone className="w-5 h-5 mr-2" />
                    Contactez nos experts
                  </Button>
                </Link>
                <Link to="/estimation-metaux">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600 px-8">
                    <TrendingUp className="w-5 h-5 mr-2" />
                    Estimation gratuite
                  </Button>
                </Link>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="mt-16 bg-gray-50 rounded-2xl p-8">
              <h2 className="text-3xl font-heading font-bold text-center mb-8 text-fabre-gray">
                Questions fréquentes
              </h2>
              
              <div className="max-w-4xl mx-auto space-y-6">
                <div className="border-l-4 border-blue-500 pl-6 bg-white p-4 rounded-r-lg">
                  <h3 className="font-semibold text-lg mb-2 text-fabre-gray">Proposez-vous des solutions pour tous les métiers ?</h3>
                  <p className="text-gray-600">Nous couvrons plus de 15 secteurs d'activité et développons des solutions sur-mesure pour tous les métiers, même les plus spécialisés.</p>
                </div>
                
                <div className="border-l-4 border-blue-500 pl-6 bg-white p-4 rounded-r-lg">
                  <h3 className="font-semibold text-lg mb-2 text-fabre-gray">Comment adaptez-vous vos services à chaque métier ?</h3>
                  <p className="text-gray-600">Nous analysons les déchets spécifiques à votre activité, vos contraintes opérationnelles et réglementaires pour proposer la solution la plus adaptée.</p>
                </div>
                
                <div className="border-l-4 border-blue-500 pl-6 bg-white p-4 rounded-r-lg">
                  <h3 className="font-semibold text-lg mb-2 text-fabre-gray">Assurez-vous la conformité réglementaire ?</h3>
                  <p className="text-gray-600">Oui, nous garantissons le respect de toutes les réglementations spécifiques à votre secteur et fournissons toute la documentation nécessaire.</p>
                </div>

                <div className="border-l-4 border-blue-500 pl-6 bg-white p-4 rounded-r-lg">
                  <h3 className="font-semibold text-lg mb-2 text-fabre-gray">Combien coûte une solution sur-mesure ?</h3>
                  <p className="text-gray-600">Nos tarifs dépendent de vos volumes et spécificités. Nous proposons toujours un devis gratuit et personnalisé après analyse de vos besoins.</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default MetiersIndex;
